import type {
  Address, AffiliateSummary, Coupon, LoyaltyAccount, LoyaltyTransaction, Order, User, WalletTransaction,
} from '@/types';
import { productsSeed } from './catalog';

export const demoUser: User = {
  id: 'usr_demo',
  name: 'Aarav Sharma',
  email: 'aarav.sharma@example.com',
  phone: '+91 98765 43210',
  role: 'CUSTOMER',
  tenantId: 'tenant_demo',
  memberSince: '2025-11-04T00:00:00Z',
};

export const demoAddresses: Address[] = [
  { id: 'addr_1', label: 'Home', name: 'Aarav Sharma', phone: '+91 98765 43210', line1: '42, Lakeview Apartments', line2: 'HSR Layout, Sector 2', city: 'Bengaluru', state: 'Karnataka', pincode: '560102', isDefault: true },
  { id: 'addr_2', label: 'Office', name: 'Aarav Sharma', phone: '+91 98765 43210', line1: '7th Floor, Prestige Tech Park', city: 'Bengaluru', state: 'Karnataka', pincode: '560103' },
];

export const walletSeed: WalletTransaction[] = [
  { id: 'wtx_6', type: 'REFUND', amount: 500, balanceAfter: 1200, description: 'Refund — Order #MCE-100412', createdAt: '2026-09-12T10:22:00Z' },
  { id: 'wtx_5', type: 'ORDER_PAYMENT', amount: -300, balanceAfter: 700, description: 'Order payment — #MCE-100398', createdAt: '2026-09-04T18:05:00Z' },
  { id: 'wtx_4', type: 'REWARD', amount: 100, balanceAfter: 1000, description: 'Cashback — #MCE-100371', createdAt: '2026-08-28T09:00:00Z' },
  { id: 'wtx_3', type: 'TOP_UP', amount: 500, balanceAfter: 900, description: 'Wallet top-up via UPI', createdAt: '2026-08-15T14:41:00Z' },
  { id: 'wtx_2', type: 'ORDER_PAYMENT', amount: -1200, balanceAfter: 400, description: 'Order payment — #MCE-100305', createdAt: '2026-07-30T20:12:00Z' },
  { id: 'wtx_1', type: 'TOP_UP', amount: 1600, balanceAfter: 1600, description: 'Wallet top-up via Card', createdAt: '2026-07-20T11:00:00Z' },
];

export const loyaltySeed: { account: LoyaltyAccount; transactions: LoyaltyTransaction[] } = {
  account: {
    balance: 120, lifetimeEarned: 340, lifetimeRedeemed: 220, tier: 'Silver',
    nextTier: { name: 'Gold', pointsNeeded: 160 },
    earnRules: [
      { id: 'r1', label: '₹100 spent = 1 point', points: 1 },
      { id: 'r2', label: 'Sign up bonus', points: 20, done: true },
      { id: 'r3', label: 'Write a product review', points: 20 },
      { id: 'r4', label: 'Refer a friend', points: 50 },
    ],
    rewards: [
      { id: 'rw1', title: '₹50 off your order', cost: 50, description: 'Redeem at checkout on any order.' },
      { id: 'rw2', title: '₹120 off your order', cost: 100, description: 'Best value — applies to orders above ₹1,000.' },
      { id: 'rw3', title: 'Free express shipping', cost: 80, description: 'One-time express delivery upgrade.' },
    ],
  },
  transactions: [
    { id: 'ltx_6', type: 'EARN', points: 35, description: 'Purchase — #MCE-100412', createdAt: '2026-09-12T10:22:00Z' },
    { id: 'ltx_5', type: 'REDEEM', points: -30, description: 'Redeemed at checkout — #MCE-100398', createdAt: '2026-09-04T18:05:00Z' },
    { id: 'ltx_4', type: 'BONUS', points: 20, description: 'Review reward — Wireless Earbuds Pro', createdAt: '2026-08-29T08:10:00Z' },
    { id: 'ltx_3', type: 'EARN', points: 12, description: 'Purchase — #MCE-100371', createdAt: '2026-08-28T09:00:00Z' },
    { id: 'ltx_2', type: 'EXPIRE', points: -10, description: 'Points expired', createdAt: '2026-08-01T00:00:00Z' },
    { id: 'ltx_1', type: 'BONUS', points: 20, description: 'Sign up bonus', createdAt: '2025-11-04T00:00:00Z' },
  ],
};

export const couponsSeed: Coupon[] = [
  { id: 'cpn_1', code: 'WELCOME10', title: '10% off your first order', description: '10% off up to ₹300 on orders above ₹999.', type: 'PERCENTAGE', value: 10, maxDiscount: 300, minOrder: 999, expiresAt: '2027-03-31T23:59:00Z', usageLimit: 1000, usedCount: 312, perCustomerLimit: 1 },
  { id: 'cpn_2', code: 'FLAT200', title: 'Flat ₹200 off', description: 'Flat ₹200 off on orders above ₹1,999.', type: 'FIXED', value: 200, minOrder: 1999, expiresAt: '2026-12-31T23:59:00Z', usageLimit: 500, usedCount: 141, perCustomerLimit: 2 },
  { id: 'cpn_3', code: 'AUDIO15', title: '15% off Electronics', description: '15% off up to ₹800 on electronics above ₹2,000.', type: 'PERCENTAGE', value: 15, maxDiscount: 800, minOrder: 2000, expiresAt: '2026-11-30T23:59:00Z', usageLimit: 300, usedCount: 87, perCustomerLimit: 1, categorySlugs: ['electronics'] },
  { id: 'cpn_4', code: 'FESTIVE25', title: '25% festive offer', description: '25% off — ended.', type: 'PERCENTAGE', value: 25, maxDiscount: 1000, minOrder: 1500, expiresAt: '2026-08-31T23:59:00Z', usageLimit: 200, usedCount: 200, perCustomerLimit: 1 },
];

/** Codes the demo user has already used (drives the "Used / Expired" tab). */
export const usedCouponCodes = ['FESTIVE25'];

export const affiliateSeed: AffiliateSummary = {
  referralCode: 'AARAV25',
  referralLink: 'https://demo.mce.store/?ref=AARAV25',
  clicks: 184, signups: 22, conversions: 9, commissionRate: 5,
  pendingCommission: 340, paidCommission: 1250,
  referrals: [
    { id: 'ref_1', name: 'Neha K.', status: 'PURCHASED', commission: 145, date: '2026-09-10T00:00:00Z' },
    { id: 'ref_2', name: 'Vikram S.', status: 'SIGNED_UP', commission: 0, date: '2026-09-08T00:00:00Z' },
    { id: 'ref_3', name: 'Ritu P.', status: 'PURCHASED', commission: 195, date: '2026-08-30T00:00:00Z' },
  ],
};

const P = (slug: string) => productsSeed.find((p) => p.slug === slug)!;
const item = (slug: string, quantity = 1, variant?: Record<string, string>) => {
  const p = P(slug);
  return { productId: p.id, slug: p.slug, name: p.name, price: p.price, quantity, icon: p.icon, hue: p.hue, variant };
};
const pricing = (subtotal: number, discount = 0, wallet = 0, points = 0) => {
  const shipping = subtotal >= 999 ? 0 : 79;
  const total = subtotal - discount + shipping;
  return { subtotal, couponDiscount: discount, walletApplied: wallet, pointsApplied: points, shipping, taxIncluded: Math.round((total * 18) / 118), total, payable: total - wallet - points };
};
const addr = demoAddresses[0];

export const ordersSeed: Order[] = [
  {
    id: 'ord_5', number: 'MCE-100412', status: 'SHIPPED', placedAt: '2026-09-15T09:30:00Z',
    items: [item('wireless-noise-cancelling-headphones', 1, { Colour: 'Midnight Black' }), item('ergonomic-wireless-mouse')],
    pricing: pricing(4298, 300), paymentMethod: 'UPI', paymentStatus: 'PAID', address: addr, couponCode: 'WELCOME10',
    tracking: { carrier: 'BlueDart', awb: 'BD8829105521' },
    timeline: [
      { status: 'PENDING', at: '2026-09-15T09:30:00Z', note: 'Order placed' },
      { status: 'PAID', at: '2026-09-15T09:31:00Z', note: 'Payment received via UPI' },
      { status: 'PROCESSING', at: '2026-09-15T13:10:00Z', note: 'Packed and ready' },
      { status: 'SHIPPED', at: '2026-09-16T08:45:00Z', note: 'Shipped with BlueDart' },
    ],
  },
  {
    id: 'ord_4', number: 'MCE-100398', status: 'DELIVERED', placedAt: '2026-09-02T16:12:00Z',
    items: [item('cushioned-running-shoes', 1, { 'Size (UK)': '9', Colour: 'Blue/White' })],
    pricing: pricing(2499, 0, 300, 30), paymentMethod: 'WALLET', paymentStatus: 'PAID', address: addr,
    timeline: [
      { status: 'PENDING', at: '2026-09-02T16:12:00Z', note: 'Order placed' },
      { status: 'PAID', at: '2026-09-02T16:13:00Z', note: 'Paid with wallet + points' },
      { status: 'SHIPPED', at: '2026-09-03T10:00:00Z' },
      { status: 'DELIVERED', at: '2026-09-06T14:20:00Z', note: 'Delivered' },
    ],
  },
  {
    id: 'ord_3', number: 'MCE-100371', status: 'DELIVERED', placedAt: '2026-08-24T11:05:00Z',
    items: [item('wireless-earbuds-pro', 1, { Colour: 'White' }), item('insulated-steel-water-bottle-1l', 2, { Colour: 'Matte Black' })],
    pricing: pricing(3997), paymentMethod: 'CARD', paymentStatus: 'PAID', address: addr,
    timeline: [
      { status: 'PENDING', at: '2026-08-24T11:05:00Z' },
      { status: 'PAID', at: '2026-08-24T11:06:00Z' },
      { status: 'SHIPPED', at: '2026-08-25T09:30:00Z' },
      { status: 'DELIVERED', at: '2026-08-28T12:00:00Z' },
    ],
  },
  {
    id: 'ord_2', number: 'MCE-100305', status: 'CANCELLED', placedAt: '2026-07-30T20:10:00Z',
    items: [item('classic-denim-jacket', 1, { Size: 'M' })],
    pricing: pricing(2799), paymentMethod: 'COD', paymentStatus: 'FAILED', address: addr,
    timeline: [
      { status: 'PENDING', at: '2026-07-30T20:10:00Z' },
      { status: 'CANCELLED', at: '2026-07-31T09:00:00Z', note: 'Cancelled by customer' },
    ],
  },
  {
    id: 'ord_1', number: 'MCE-100212', status: 'REFUNDED', placedAt: '2026-07-01T10:00:00Z',
    items: [item('portable-bluetooth-speaker', 1, { Colour: 'Teal' })],
    pricing: pricing(1999), paymentMethod: 'UPI', paymentStatus: 'REFUNDED', address: addr,
    timeline: [
      { status: 'PENDING', at: '2026-07-01T10:00:00Z' },
      { status: 'PAID', at: '2026-07-01T10:01:00Z' },
      { status: 'DELIVERED', at: '2026-07-05T15:00:00Z' },
      { status: 'REFUNDED', at: '2026-07-12T11:00:00Z', note: 'Returned — refunded to source' },
    ],
  },
];
